import { Bus, MapPin, Shield } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Separator } from '~/components/ui/separator';
import { SignatureField } from '~/components/forms/SignatureField';

/**
 * Field Trip Release Step for a single student
 *
 * - Field trip participation consent (yes/no)
 * - Transportation consent (yes/no)
 * - Typed signature
 */
export function FieldTripReleaseStep({
  student,
  release,
  onReleaseChange
}: any) {
  const { firstName, lastName } = student;
  const studentName = `${firstName} ${lastName}`;

  const handleConsentChange = (value: string) => {
    onReleaseChange({
      consent: value === 'yes'
    });
  };

  const handleTransportChange = (value: string) => {
    onReleaseChange({
      transportConsent: value === 'yes'
    });
  };

  const handleSignatureChange = (sig: string) => {
    onReleaseChange({
      signature: sig,
      signedAt: sig ? new Date().toISOString() : null
    });
  };

  const consentValue = release.consent === true ? 'yes'
    : release.consent === false ? 'no'
    : undefined;

  const transportValue = release.transportConsent === true ? 'yes'
    : release.transportConsent === false ? 'no'
    : undefined;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
          <MapPin className="h-6 w-6 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">Field Trip Release</h2>
        <p className="text-muted-foreground">For {studentName}</p>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg text-sm">
        <p>
          Throughout the school year, students may participate in educational field trips
          to enhance their learning experience. These trips may include visits to museums,
          parks, local businesses, and other educational venues.
        </p>
      </div>

      {/* Field Trip Participation */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Label className="text-base font-medium">Field Trip Participation</Label>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Do you give permission for {firstName} to participate in school-sponsored field trips
            during the school year?
          </p>
        </div>

        <RadioGroup
          value={consentValue}
          onValueChange={handleConsentChange}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="fieldtrip-yes" />
            <Label htmlFor="fieldtrip-yes" className="font-normal cursor-pointer">
              Yes, {firstName} may participate in field trips
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="fieldtrip-no" />
            <Label htmlFor="fieldtrip-no" className="font-normal cursor-pointer">
              No, {firstName} may not participate in field trips
            </Label>
          </div>
        </RadioGroup>
      </div>

      <Separator />

      {/* Transportation Consent */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Bus className="h-4 w-4 text-muted-foreground" />
            <Label className="text-base font-medium">Transportation Authorization</Label>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Do you authorize {firstName} to travel on school-provided transportation (bus, van,
            or other school vehicles) for field trips and school activities?
          </p>
        </div>

        <RadioGroup
          value={transportValue}
          onValueChange={handleTransportChange}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="transport-yes" />
            <Label htmlFor="transport-yes" className="font-normal cursor-pointer">
              Yes, {firstName} may use school transportation
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="transport-no" />
            <Label htmlFor="transport-no" className="font-normal cursor-pointer">
              No, I will provide my own transportation
            </Label>
          </div>
        </RadioGroup>

        {release.transportConsent === false && release.consent === true && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <Shield className="h-4 w-4 inline mr-2" />
            You will be responsible for transporting {firstName} to and from all field trip locations.
          </div>
        )}
      </div>

      <Separator />

      <SignatureField
        value={release.signature || ''}
        onChange={handleSignatureChange}
        label="Electronic Signature"
        description={`By typing your name below, you confirm your field trip and transportation selections for ${studentName}.`}
        id="fieldtrip-signature"
        asCard={false}
      />
    </div>
  );
}

export default FieldTripReleaseStep;
